import React, { useEffect, useRef, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiSend, FiMic, FiStopCircle, FiCpu, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import ModalEnviarEmailCliente from '../components/ui/ModalEnviarEmailCliente';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';
import { useSession } from '../contexts/SessionContext';

const ESTADOS_CRM = ['prospecto', 'contactado', 'presupuesto_enviado', 'negociando', 'ganado', 'perdido'];

const BADGE_CRM = {
  prospecto: 'badge',
  contactado: 'badge-info',
  presupuesto_enviado: 'badge-warning',
  negociando: 'badge-warning',
  ganado: 'badge-ok',
  perdido: 'badge-danger',
};

const ETIQUETA_CRM = {
  prospecto: 'Prospecto',
  contactado: 'Contactado',
  presupuesto_enviado: 'Presupuesto enviado',
  negociando: 'Negociando',
  ganado: 'Ganado',
  perdido: 'Perdido',
};

const VACIO = {
  nombre: '', nif: '', email: '', telefono: '',
  direccion: '', poblacion: '', provincia: '', codigoPostal: '', notas: '',
  estadoCrm: '', motivoPerdida: '', proximoContacto: '',
  personasContacto: [],
};

function validarNifCif(valorOriginal) {
  const valor = String(valorOriginal || '').toUpperCase().replace(/[\s-]/g, '');
  const tablaDni = 'TRWAGMYFPDXBNJZSQVHLCKE';
  if (/^\d{8}[A-Z]$/.test(valor)) {
    const numero = parseInt(valor.slice(0, 8), 10);
    return valor[8] === tablaDni[numero % 23];
  }
  if (/^[XYZ]\d{7}[A-Z]$/.test(valor)) {
    const prefijo = { X: '0', Y: '1', Z: '2' }[valor[0]];
    const numero = parseInt(prefijo + valor.slice(1, 8), 10);
    return valor[8] === tablaDni[numero % 23];
  }
  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(valor)) {
    const digitos = valor.slice(1, 8);
    let sumaPar = 0;
    let sumaImpar = 0;
    for (let i = 0; i < digitos.length; i += 1) {
      const d = parseInt(digitos[i], 10);
      if ((i + 1) % 2 === 0) {
        sumaPar += d;
      } else {
        const doble = d * 2;
        sumaImpar += doble > 9 ? doble - 9 : doble;
      }
    }
    const digitoControl = (10 - ((sumaPar + sumaImpar) % 10)) % 10;
    const letraControl = 'JABCDEFGHI'[digitoControl];
    const ultimo = valor[8];
    return ultimo === String(digitoControl) || ultimo === letraControl;
  }
  return false;
}

function validarEmailCliente(valor) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(valor || ''));
}

function validarTelefonoCliente(valorOriginal) {
  const valor = String(valorOriginal || '').replace(/[\s-]/g, '');
  return /^(?:\+34|0034)?[6789]\d{8}$/.test(valor);
}

const ESTADO_INICIAL = 'prospecto';
const MAX_AUDIO_BYTES = 25 * 1024 * 1024;
const MIME_AUDIO_PERMITIDOS = [
  'audio/webm', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/m4a', 'audio/x-m4a', 'audio/mp4',
  'video/webm', 'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/3gpp'
];
const MIME_POR_EXTENSION = {
  webm: 'audio/webm', wav: 'audio/wav', mp3: 'audio/mpeg', ogg: 'audio/ogg', m4a: 'audio/m4a', mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo', '3gp': 'video/3gpp'
};

function validarAudio(file, nombre) {
  if (!file) return { ok: false, error: 'Graba o selecciona un archivo de audio o vídeo primero.' };
  if (file.size > MAX_AUDIO_BYTES) return { ok: false, error: 'El archivo supera el máximo permitido (25 MB).' };
  let mimeType = (file.type || '').split(';')[0].trim().toLowerCase();
  if (!MIME_AUDIO_PERMITIDOS.includes(mimeType)) mimeType = MIME_POR_EXTENSION[((nombre || '').split('.').pop() || '').toLowerCase()] || mimeType;
  if (!MIME_AUDIO_PERMITIDOS.includes(mimeType)) return { ok: false, error: 'Formato no soportado. Usa webm, wav, mp3, mp4, ogg, m4a, mov o avi.' };
  return { ok: true, mimeType };
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ClienteVoz({ onAplicar, onCerrar }) {
  const [archivo, setArchivo] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [grabando, setGrabando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [borrador, setBorrador] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const asignarArchivo = (file, nombre) => {
    if (!file) return;
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setArchivo(file);
    setNombreArchivo(nombre);
    setAudioUrl(URL.createObjectURL(file));
    setError('');
    setBorrador(null);
  };

  const iniciarGrabacion = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tipos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];
      const mimeType = tipos.find((tipo) => window.MediaRecorder.isTypeSupported?.(tipo));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (evento) => { if (evento.data.size) chunksRef.current.push(evento.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        asignarArchivo(new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' }), 'cliente.webm');
      };
      recorderRef.current = recorder;
      recorder.start();
      setGrabando(true);
    } catch (_err) {
      setError('No se pudo acceder al micrófono. Revisa los permisos del navegador.');
    }
  };

  const detenerGrabacion = () => {
    recorderRef.current?.stop();
    setGrabando(false);
  };

  const procesar = async () => {
    const validacion = validarAudio(archivo, nombreArchivo);
    if (!validacion.ok) { setError(validacion.error); return; }
    setCargando(true);
    setError('');
    try {
      setMensaje('Transcribiendo audio...');
      const audio = await toBase64(archivo);
      const { data: transcripcion } = await api.post('/presupuestos-ia/transcribir-cliente', { audio, mimeType: validacion.mimeType });
      setMensaje('Extrayendo datos del cliente...');
      const { data } = await api.post('/presupuestos-ia/generar-cliente-desde-voz', { texto: transcripcion.texto });
      setBorrador(data);
      setMensaje('Revisa los datos antes de aplicarlos.');
    } catch (errProcesar) {
      const errorApi = errProcesar?.response?.data?.error;
      const estadoApi = errProcesar?.response?.status ? ` (HTTP ${errProcesar.response.status})` : '';
      const codigoApi = errProcesar?.response?.data?.codigo ? ` [${errProcesar.response.data.codigo}]` : '';
      setError(errorApi ? `${errorApi}${codigoApi}${estadoApi}` : (errProcesar?.message ? `${errProcesar.message}${estadoApi}` : 'No se pudo procesar el audio. Comprueba los logs del backend.'));
    } finally {
      setCargando(false);
    }
  };

  const cerrar = () => {
    if (grabando) recorderRef.current?.stop();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    onCerrar();
  };

  return (
    <div>
      <div className="alert-info" style={{ marginBottom: 14 }}>
        <strong>Qué debes decir:</strong><br />
        Debes describir nombre, apellidos, empresa, dirección, población, provincia, CIF/NIF, teléfono y email del cliente. Por ejemplo: <em>Cliente, nombre Juan Pérez, dirección Calle Mayor 12, población Madrid, CIF B12345678, teléfono 612345678, email juan@ejemplo.com.</em>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" className={grabando ? 'btn btn-danger' : 'btn'} onClick={grabando ? detenerGrabacion : iniciarGrabacion} disabled={cargando}>
          {grabando ? <><FiStopCircle /> Detener grabación</> : <><FiMic /> Grabar cliente</>}
        </button>
        <label className="btn">
          <FiPlus /> Subir audio / vídeo
          <input type="file" accept="audio/*,video/*" hidden disabled={grabando || cargando} onChange={(e) => asignarArchivo(e.target.files?.[0], e.target.files?.[0]?.name)} />
        </label>
      </div>
      {audioUrl ? <audio controls src={audioUrl} style={{ width: '100%', marginBottom: 12 }}>Tu navegador no soporta audio.</audio> : null}
      {mensaje ? <div className="alert-ok">{mensaje}</div> : null}
      {error ? <div className="alert-error">{error}</div> : null}
      {borrador ? (
        <div className="table-wrap" style={{ marginTop: 14, padding: 14 }}>
          <strong><FiCpu /> Borrador detectado</strong>
          <p className="muted">Campos detectados: {borrador.camposDetectados?.join(', ') || 'ninguno'}</p>
          {borrador.avisos?.length ? <div className="alert-warning">{borrador.avisos.map((aviso) => aviso.mensaje).join(' ')}</div> : null}
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {Object.entries(borrador.datos || {}).map(([campo, valor]) => (
              <label className="field" key={campo}><span>{campo}</span><input value={valor || ''} onChange={(e) => setBorrador((prev) => ({ ...prev, datos: { ...prev.datos, [campo]: e.target.value || null } }))} /></label>
            ))}
          </div>
          <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => onAplicar(borrador.datos)} disabled={!borrador.datos?.nombre}>
            Aplicar datos al formulario
          </button>
        </div>
      ) : null}
      <div className="form-actions" style={{ marginTop: 16 }}><button type="button" className="btn" onClick={cerrar}>Cerrar</button><button type="button" className="btn btn-primary" onClick={procesar} disabled={!archivo || cargando || grabando}>{cargando ? 'Procesando...' : 'Transcribir y preparar cliente'}</button></div>
    </div>
  );
}

function ClienteFormulario({ valores, onGuardar, onCancelar, guardando, onDirtyChange }) {
  const { usuario } = useAuth();
  const formularioRef = useRef(null);
  const [formState, setFormState] = useState({
    nombre: valores.nombre || '',
    nif: valores.nif || '',
    email: valores.email || '',
    telefono: valores.telefono || '',
    direccion: valores.direccion || '',
    poblacion: valores.poblacion || '',
    provincia: valores.provincia || '',
    codigoPostal: valores.codigoPostal || '',
    notas: valores.notas || '',
    motivoPerdida: valores.motivoPerdida || '',
    proximoContacto: valores.proximoContacto || '',
    estadoCrm: valores.estadoCrm || ESTADO_INICIAL,
  });
  const [estadoCrm, setEstadoCrm] = useState(valores.estadoCrm || ESTADO_INICIAL);
  const [documentosFirmados, setDocumentosFirmados] = useState([]);
  const [documentosFirmadosCargando, setDocumentosFirmadosCargando] = useState(false);
  const [consultandoPostal, setConsultandoPostal] = useState(false);
  const [mensajePostal, setMensajePostal] = useState('');
  const [errores, setErrores] = useState({});

  useEffect(() => {
    const nextState = {
      nombre: valores.nombre || '',
      nif: valores.nif || '',
      email: valores.email || '',
      telefono: valores.telefono || '',
      direccion: valores.direccion || '',
      poblacion: valores.poblacion || '',
      provincia: valores.provincia || '',
      codigoPostal: valores.codigoPostal || '',
      notas: valores.notas || '',
      motivoPerdida: valores.motivoPerdida || '',
      proximoContacto: valores.proximoContacto || '',
      estadoCrm: valores.estadoCrm || ESTADO_INICIAL,
    };
    setFormState(nextState);
    setEstadoCrm(valores.estadoCrm || ESTADO_INICIAL);
  }, [valores]);

  useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  useEffect(() => {
    setFormState((prev) => ({ ...prev, estadoCrm }));
  }, [estadoCrm]);

  useEffect(() => {
    const handleDirty = () => onDirtyChange?.(true);
    const form = formularioRef.current;
    if (!form) return undefined;
    form.addEventListener('input', handleDirty);
    form.addEventListener('change', handleDirty);
    return () => {
      form.removeEventListener('input', handleDirty);
      form.removeEventListener('change', handleDirty);
    };
  }, [onDirtyChange]);
  const [personasContacto, setPersonasContacto] = useState(
    Array.isArray(valores.personasContacto) ? valores.personasContacto : []
  );

  const manejarTeclaEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const puedeVerDocumentos = puede(usuario, 'clientes', 'ver') || puede(usuario, 'presupuestos', 'ver');
    if (!valores?._id || !puedeVerDocumentos) {
      setDocumentosFirmados([]);
      return;
    }

    const cargarDocumentosFirmados = async () => {
      try {
        setDocumentosFirmadosCargando(true);
        const { data } = await api.get('/firma/documentos', { params: { clienteId: valores._id } });
        setDocumentosFirmados(Array.isArray(data) ? data : []);
      } catch {
        setDocumentosFirmados([]);
      } finally {
        setDocumentosFirmadosCargando(false);
      }
    };

    cargarDocumentosFirmados();
  }, [usuario, valores?._id]);

  const anadirPersonaContacto = () => {
    setPersonasContacto((prev) => [
      ...prev,
      { nombre: '', apellidos: '', telefono: '', email: '' },
    ]);
  };

  const actualizarPersonaContacto = (index, campo, valor) => {
    setPersonasContacto((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [campo]: valor } : item))
    );
  };

  const eliminarPersonaContacto = (index) => {
    setPersonasContacto((prev) => prev.filter((_, i) => i !== index));
  };

  const buscarCodigoPostal = async (e) => {
    const codigoPostal = String((e?.target?.value || formState.codigoPostal || '')).trim();
    setMensajePostal('');
    if (!/^\d{5}$/.test(codigoPostal)) return;

    try {
      setConsultandoPostal(true);
      const { data } = await api.get(`/clientes/buscar-codigo-postal/${codigoPostal}`);
      const lugar = data?.resultados?.[0];
      if (!lugar) {
        setMensajePostal('No se encontro una localidad para ese codigo postal.');
        return;
      }
      setFormState((prev) => ({
        ...prev,
        codigoPostal,
        poblacion: lugar.poblacion || prev.poblacion,
        provincia: lugar.provincia || prev.provincia,
      }));
      setMensajePostal(`Direccion completada: ${[lugar.poblacion, lugar.provincia].filter(Boolean).join(', ')}`);
    } catch (errPostal) {
      setMensajePostal(errPostal?.response?.data?.error || 'No se pudo consultar el codigo postal.');
    } finally {
      setConsultandoPostal(false);
    }
  };

  const enviar = (e) => {
    e.preventDefault();
    const datos = {
      ...formState,
      nombre: String(formState.nombre || '').trim(),
      nif: String(formState.nif || '').trim(),
      email: String(formState.email || '').trim(),
      telefono: String(formState.telefono || '').trim(),
      direccion: String(formState.direccion || '').trim(),
      poblacion: String(formState.poblacion || '').trim(),
      provincia: String(formState.provincia || '').trim(),
      codigoPostal: String(formState.codigoPostal || '').trim(),
      notas: String(formState.notas || '').trim(),
      motivoPerdida: String(formState.motivoPerdida || '').trim(),
      proximoContacto: String(formState.proximoContacto || '').trim(),
      estadoCrm: String(estadoCrm || '').trim() || ESTADO_INICIAL,
    };
    const nuevosErrores = {};
    if (!datos.nombre?.trim()) nuevosErrores.nombre = 'El nombre del cliente es obligatorio.';
    if (datos.nif && !validarNifCif(datos.nif)) nuevosErrores.nif = 'El NIF/CIF no tiene un formato válido';
    if (!datos.nombre?.trim() || !(datos.email?.trim() || datos.telefono?.trim())) {
      nuevosErrores.paso = 'El nombre y al menos un contacto (email o teléfono) son obligatorios.';
    }
    if (datos.email && !validarEmailCliente(datos.email)) nuevosErrores.email = 'El email no tiene un formato válido';
    if (datos.telefono && !validarTelefonoCliente(datos.telefono)) nuevosErrores.telefono = 'El teléfono no tiene un formato válido (debe ser un móvil o fijo español de 9 dígitos)';
    if (datos.estadoCrm !== 'perdido') datos.motivoPerdida = '';

    for (let i = 0; i < personasContacto.length; i += 1) {
      const pc = personasContacto[i];
      if (!pc.nombre?.trim() || !pc.apellidos?.trim() || !pc.telefono?.trim() || !pc.email?.trim()) {
        nuevosErrores.personasContacto = `Todos los campos (nombre, apellidos, teléfono y email) son obligatorios para la persona de contacto #${i + 1}.`;
        break;
      }
      if (!validarEmailCliente(pc.email)) {
        nuevosErrores.personasContacto = `El email de la persona de contacto #${i + 1} no es válido.`;
        break;
      }
      if (!validarTelefonoCliente(pc.telefono)) {
        nuevosErrores.personasContacto = `El teléfono de la persona de contacto #${i + 1} no es válido (debe ser un teléfono de 9 dígitos).`;
        break;
      }
    }

    if (Object.keys(nuevosErrores).length) { setErrores(nuevosErrores); return; }
    setErrores({});
    datos.personasContacto = personasContacto.map((pc) => ({
      nombre: pc.nombre.trim(),
      apellidos: pc.apellidos.trim(),
      telefono: pc.telefono.trim(),
      email: pc.email.trim().toLowerCase(),
    }));
    onGuardar(datos);
  };

  return (
    <form ref={formularioRef} onSubmit={enviar} onKeyDown={manejarTeclaEnter} className="form-grid">
      {errores.paso && <div className="alert-error span-2" style={{ padding: '10px 12px', fontSize: '0.86rem' }}>{errores.paso}</div>}

      <label className="field span-2"><span>Nombre del cliente *</span><input name="nombre" required value={formState.nombre} onChange={(e) => setFormState((prev) => ({ ...prev, nombre: e.target.value }))} placeholder="Ej: Juan Pérez" /></label>
      <label className="field"><span>Email</span><input name="email" type="email" value={formState.email} onChange={(e) => setFormState((prev) => ({ ...prev, email: e.target.value }))} placeholder="ejemplo@empresa.com" />{errores.email ? <span className="field-error" style={{ color: '#dc2626', fontSize: 12 }}>{errores.email}</span> : null}</label>
      <label className="field"><span>Teléfono</span><input name="telefono" value={formState.telefono} onChange={(e) => setFormState((prev) => ({ ...prev, telefono: e.target.value }))} placeholder="600 123 456" />{errores.telefono ? <span className="field-error" style={{ color: '#dc2626', fontSize: 12 }}>{errores.telefono}</span> : null}</label>
      <label className="field span-2"><span>Dirección</span><input name="direccion" value={formState.direccion} onChange={(e) => setFormState((prev) => ({ ...prev, direccion: e.target.value }))} placeholder="Calle Mayor 12" /></label>
      <label className="field"><span>Código postal</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input name="codigoPostal" inputMode="numeric" maxLength={5} value={formState.codigoPostal} onChange={(e) => setFormState((prev) => ({ ...prev, codigoPostal: e.target.value }))} placeholder="28001" style={{ flex: 1 }} />
          <button type="button" className="btn btn-sm" onClick={() => buscarCodigoPostal({ target: { value: formState.codigoPostal || '' } })}>Buscar</button>
        </div>
      </label>
      <label className="field"><span>Población</span><input name="poblacion" value={formState.poblacion} onChange={(e) => setFormState((prev) => ({ ...prev, poblacion: e.target.value }))} placeholder="Madrid" /></label>
      <label className="field span-2"><span>Provincia</span><input name="provincia" value={formState.provincia} onChange={(e) => setFormState((prev) => ({ ...prev, provincia: e.target.value }))} placeholder="Madrid" /></label>
      <div className="field span-2" style={{ minHeight: 20 }}>
        {consultandoPostal ? <span className="muted">Consultando código postal…</span> : null}
        {!consultandoPostal && mensajePostal ? <span className="muted">{mensajePostal}</span> : null}
      </div>

      <label className="field"><span>NIF / CIF (opcional)</span><input name="nif" value={formState.nif} onChange={(e) => setFormState((prev) => ({ ...prev, nif: e.target.value }))} placeholder="B12345678" />{errores.nif ? <span className="field-error" style={{ color: '#dc2626', fontSize: 12 }}>{errores.nif}</span> : null}</label>
      <label className="field span-2"><span>Notas</span><textarea name="notas" rows={3} value={formState.notas} onChange={(e) => setFormState((prev) => ({ ...prev, notas: e.target.value }))} placeholder="Cuéntanos algo importante sobre este cliente" /></label>
      <label className="field"><span>Próximo contacto</span><input name="proximoContacto" type="date" value={formState.proximoContacto} onChange={(e) => setFormState((prev) => ({ ...prev, proximoContacto: e.target.value }))} /></label>
      {estadoCrm === 'perdido' ? (
        <label className="field"><span>Motivo de pérdida</span><input name="motivoPerdida" value={formState.motivoPerdida} onChange={(e) => setFormState((prev) => ({ ...prev, motivoPerdida: e.target.value }))} /></label>
      ) : <input type="hidden" name="motivoPerdida" value="" readOnly />}

      <div className="span-2" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <strong>Personas de contacto ({personasContacto.length})</strong>
            <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>
              Si lo necesitas, añade un contacto adicional del cliente.
            </p>
          </div>
          <button type="button" className="btn mini" onClick={anadirPersonaContacto}><FiPlus /> Añadir</button>
        </div>

        {errores.personasContacto && (
          <div className="alert-error" style={{ marginBottom: 12, padding: '8px 12px', fontSize: '0.85rem' }}>
            {errores.personasContacto}
          </div>
        )}

        {personasContacto.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {personasContacto.map((pc, index) => (
              <div key={index} style={{ background: 'var(--bg-body, #f8fafc)', border: '1px solid var(--border, #e2e8f0)', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Contacto #{index + 1}</span>
                  <button type="button" className="icon-btn danger" onClick={() => eliminarPersonaContacto(index)} title="Eliminar persona de contacto">
                    <FiTrash2 />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <label className="field"><span>Nombre *</span><input required value={pc.nombre || ''} onChange={(e) => actualizarPersonaContacto(index, 'nombre', e.target.value)} placeholder="Nombre" /></label>
                  <label className="field"><span>Apellidos *</span><input required value={pc.apellidos || ''} onChange={(e) => actualizarPersonaContacto(index, 'apellidos', e.target.value)} placeholder="Apellidos" /></label>
                  <label className="field"><span>Teléfono *</span><input required value={pc.telefono || ''} onChange={(e) => actualizarPersonaContacto(index, 'telefono', e.target.value)} placeholder="600 123 456" /></label>
                  <label className="field"><span>Email *</span><input required type="email" value={pc.email || ''} onChange={(e) => actualizarPersonaContacto(index, 'email', e.target.value)} placeholder="email@empresa.com" /></label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(valores?._id && (puede(usuario, 'clientes', 'ver') || puede(usuario, 'presupuestos', 'ver'))) && (
        <div className="span-2" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
            <strong>Documentos firmados</strong>
            <span className="badge badge-info">{documentosFirmados.length}</span>
          </div>

          {documentosFirmadosCargando ? (
            <p className="muted" style={{ margin: 0 }}>Cargando documentos firmados…</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {documentosFirmados.length === 0 ? (
                <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: '14px 16px', color: '#475569' }}>
                  No hay documentos firmados asociados a este cliente todavía.
                </div>
              ) : (
                documentosFirmados.map((doc) => (
                  <details key={doc._id} open={doc.estado === 'firmada'} style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)', border: '1px solid #dfeafc', borderRadius: 12, padding: '12px 14px', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)' }}>
                    <summary style={{ cursor: 'pointer', listStyle: 'none', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '2px 2px 10px 2px', borderBottom: '1px solid rgba(148, 163, 184, 0.35)' }}>
                      <span>{doc.documento?.numero || 'Documento'}</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155', background: '#e2e8f0', borderRadius: 999, padding: '4px 8px' }}>
                        {doc.tipoDocumento === 'presupuesto' ? 'Presupuesto' : 'Factura'} · {doc.firmadoAt ? new Date(doc.firmadoAt).toLocaleString('es-ES') : 'Sin fecha'}
                      </span>
                    </summary>

                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <a href={doc.pdfUrl} target="_blank" rel="noreferrer" className="btn btn-sm">PDF enviado</a>
                        {doc.pdfFirmadoDisponible && <a href={doc.pdfFirmadoUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">PDF firmado</a>}
                      </div>

                      {doc.estado === 'firmada' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: '0.8rem', background: '#ffffff', border: '1px solid #dbeafe', borderRadius: 10, padding: 12, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)' }}>
                          <div style={{ padding: '6px 8px', borderRadius: 8, background: '#ecfdf5', border: '1px solid #bbf7d0' }}><strong>Estado:</strong> {doc.estado}</div>
                          <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>Firmado por:</strong> {doc.firmadoPorNombre || '—'}</div>
                          <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>NIF:</strong> {doc.firmadoPorNif || '—'}</div>
                          <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>Fecha:</strong> {doc.firmadoAt ? new Date(doc.firmadoAt).toLocaleString('es-ES') : '—'}</div>
                          <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>IP:</strong> {doc.ip || '—'}</div>
                          <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>Enlace:</strong> <a href={doc.publicUrl} target="_blank" rel="noreferrer">Abrir</a></div>
                          <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', wordBreak: 'break-word' }}><strong>Hash:</strong> {doc.hashDocumento || '—'}</div>
                          <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', wordBreak: 'break-word' }}><strong>Agente:</strong> {doc.userAgent || '—'}</div>
                          {doc.reemplazadaPorToken && (
                            <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', wordBreak: 'break-word' }}><strong>Reemplazado por:</strong> {doc.reemplazadaPorToken || '—'}</div>
                          )}
                          {doc.motivo && (
                            <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', wordBreak: 'break-word' }}><strong>Motivo:</strong> {doc.motivo}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </details>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className="form-actions span-2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" className="btn" onClick={onCancelar}>Cancelar</button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginLeft: 'auto' }}>
          <button type="submit" className="btn btn-primary" disabled={guardando} style={{ minWidth: 170, fontWeight: 700, padding: '12px 18px' }}>
            {guardando ? 'Guardando…' : 'Guardar cliente'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function Clientes() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const { abrirSesion } = useSession();
  const { items, cargando, error, crear, actualizar, eliminar } = useEntity('clientes');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [formDirty, setFormDirty] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroCrm, setFiltroCrm] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [modalVoz, setModalVoz] = useState(false);
  const [clienteEmailModal, setClienteEmailModal] = useState(null);
  const [notificacion, setNotificacion] = useState(null);

  const puedeCrear = puede(usuario, 'clientes', 'crear');
  const puedeEditar = puede(usuario, 'clientes', 'editar');
  const puedeEliminar = puede(usuario, 'clientes', 'eliminar');
  const puedeEnviarEmail = puede(usuario, 'clientes', 'enviar_email');

  const mostrarNotificacion = (tipo, texto, duracion = 3200) => {
    setNotificacion({ tipo, texto });
    if (duracion > 0) {
      window.setTimeout(() => setNotificacion((actual) => (actual?.texto === texto ? null : actual)), duracion);
    }
  };

  const cerrarFormularioConConfirmacion = () => {
    const hayDatosPendientes = formDirty || Object.values(form).some((valor) => Array.isArray(valor) ? valor.length > 0 : String(valor || '').trim() !== '');
    if (hayDatosPendientes && !window.confirm('Tienes datos sin guardar. ¿Quieres salir sin guardar este cliente?')) {
      return;
    }
    setModal(false);
    setEditando(null);
    setForm({ ...VACIO, personasContacto: [] });
    setFormDirty(false);
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ ...VACIO, personasContacto: [] });
    setFormDirty(false);
    setModalVoz(false);
    setModal(true);
  };

  const abrirNuevoPorVoz = () => {
    setEditando(null);
    setForm({ ...VACIO, personasContacto: [] });
    setFormDirty(false);
    setModal(false);
    setModalVoz(true);
  };
  const abrirEditar = (c) => {
    setEditando(c);
    setForm({
      ...VACIO, ...c,
      proximoContacto: c.proximoContacto ? c.proximoContacto.slice(0, 10) : '',
      personasContacto: Array.isArray(c.personasContacto) ? c.personasContacto : [],
    });
    setModal(true);
  };

  const onGuardar = async (datos) => {
    setGuardando(true);
    try {
      if (editando) await actualizar(editando._id, datos);
      else await crear(datos);
      setModal(false);
      setFormDirty(false);
      mostrarNotificacion('success', editando ? 'Cliente actualizado correctamente' : 'Cliente creado correctamente');
    } catch (err) {
      mostrarNotificacion('error', err.response?.data?.error || 'No se pudo guardar el cliente');
    } finally {
      setGuardando(false);
    }
  };

  const onEliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el cliente "${c.nombre}"?`)) return;
    try { await eliminar(c._id); mostrarNotificacion('success', 'Cliente eliminado'); } catch { mostrarNotificacion('error', 'No se pudo eliminar'); }
  };

  const filtrados = items.filter((c) => {
    const textoOk = [c.nombre, c.email, c.telefono, c.nif].join(' ').toLowerCase().includes(busqueda.toLowerCase());
    const crmOk = filtroCrm === '' || c.estadoCrm === filtroCrm;
    return textoOk && crmOk;
  });

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">{items.length} cliente(s) registrados</p>
        </div>
        <div className="head-actions">
          {puedeCrear && (
            <>
              <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Crear nuevo cliente</button>
              <button className="btn" onClick={abrirNuevoPorVoz}><FiMic /> Crear por voz</button>
            </>
          )}
        </div>
      </div>

      <div className="page-help-card" role="status">
        <div>
          <strong>Consejo</strong>
          <p>Completa el nombre, email y estado CRM para sacar el máximo partido de la agenda y los presupuestos.</p>
        </div>
        <div className="page-summary">
          <span className="page-pill">Clientes: {items.length}</span>
        </div>
      </div>

      {notificacion && (
        <div className={`alert-${notificacion.tipo}`} role="status" style={{ marginBottom: 14 }}>
          {notificacion.texto}
        </div>
      )}

      <div className="toolbar">
        <div className="search-box">
          <FiSearch />
          <input placeholder="Buscar por nombre, email, teléfono o NIF" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
        <select className="mini-select" value={filtroCrm} onChange={(e) => setFiltroCrm(e.target.value)}>
          <option value="">Todos los estados CRM</option>
          {ESTADOS_CRM.map((s) => <option key={s} value={s}>{ETIQUETA_CRM[s]}</option>)}
        </select>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : filtrados.length === 0 ? (
        <p className="empty">No hay clientes.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nombre</th><th>NIF</th><th>Email</th><th>Teléfono</th><th>Población</th><th>Estado CRM</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr key={c._id} onDoubleClick={() => abrirSesion('cliente', c._id, c.nombre)} style={{ cursor: 'pointer' }}>
                  <td data-label="Nombre"><strong>{c.nombre}</strong></td>
                  <td data-label="NIF">{c.nif || '—'}</td>
                  <td data-label="Email">{c.email || '—'}</td>
                  <td data-label="Teléfono">{c.telefono || '—'}</td>
                  <td data-label="Población">{c.poblacion || '—'}</td>
                  <td data-label="Estado CRM">
                    {c.estadoCrm ? (
                      <span className={`badge ${BADGE_CRM[c.estadoCrm] || 'badge'}`}>{ETIQUETA_CRM[c.estadoCrm] || c.estadoCrm}</span>
                    ) : '—'}
                  </td>
                  <td className="acciones">
                    {c.email && puedeEnviarEmail && (
                      <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setClienteEmailModal(c); }} title="Enviar correo electrónico ahora"><FiMail /></button>
                    )}
                    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); abrirSesion('cliente', c._id, c.nombre); }} title="Abrir sesión">Abrir</button>
                    {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(c)} title="Editar"><FiEdit2 /></button>}
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(c)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={cerrarFormularioConConfirmacion} titulo={editando ? 'Editar cliente' : 'Nuevo cliente'} ancho={620}>
        <ClienteFormulario
          key={editando?._id || 'nuevo'}
          valores={form}
          onGuardar={onGuardar}
          onCancelar={cerrarFormularioConConfirmacion}
          guardando={guardando}
          onDirtyChange={setFormDirty}
        />
      </Modal>
      <Modal abierto={modalVoz} onCerrar={() => setModalVoz(false)} titulo="Nuevo cliente por voz" ancho={720}>
        <ClienteVoz
          onCerrar={() => setModalVoz(false)}
          onAplicar={(datos) => { setEditando(null); setForm({ ...VACIO, ...datos, estadoCrm: datos.estadoCrm || ESTADO_INICIAL }); setModalVoz(false); setModal(true); }}
        />
      </Modal>
      <ModalEnviarEmailCliente
        abierto={!!clienteEmailModal}
        onCerrar={() => setClienteEmailModal(null)}
        cliente={clienteEmailModal}
        onEnviado={() => mostrarNotificacion('success', 'Correo electrónico enviado al cliente correctamente.')}
      />
    </div>
  );
}
